/*
 * The server's data as React Query hooks. Shapes come from @steward/shared, the same ones the server checks against.
 */
import type {
  CancelBy,
  CancelReason,
  PlanOfferAnswer,
  PlanOfferPreview,
  JobCosts,
  JobExpenseInput,
  JobExpensePatch,
  JobExpenseRow,
  AlertsAnswer,
  FeedAnswer,
  ApplicantRow,
  HiringReport,
  ApplicantPatch,
  ApplicantInput,
  AutomationDetail,
  AutomationKind,
  AutomationPatch,
  AutomationRow,
  BestOrderAnswer,
  RoutePathAnswer,
  MapSpotsAnswer,
  TeamSpot,
  CapacityDay,
  DayTextAnswer,
  DayTextBody,
  LooseEnd,
  RouteInput,
  RouteRow,
  TimeOffInput,
  TimeOffRow,
  ComplaintInput,
  EuniceAnswer,
  LessonRow,
  ThreadDetail,
  ThreadRow,
  UndoneMessage,
  ComplaintPatch,
  ComplaintRow,
  SignupRow,
  SmartListInput,
  SmartListRow,
  MyPerson,
} from "@steward/shared";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PricingRow,
  ReminderInput,
  ReminderRow,
  auditItem,
  BulkAnswer,
  ChecklistRow,
  ClientDetail,
  clientInput,
  clientPatch,
  ClientRow,
  ClientSummary,
  companyAnswer,
  landingAnswer,
  signInLookAnswer,
  companySummaryRow,
  contactInput,
  deactivateBody,
  fileUpload,
  HomeAnswer,
  companyUpdateBody,
  inviteAnswer,
  inviteBody,
  invitePeek,
  JobDetail,
  jobInput,
  jobPatch,
  JobRow,
  keyRow,
  lineInput,
  meAnswer,
  MergeAnswer,
  myVisit,
  noteInput,
  notePatch,
  outboxItem,
  personPatch,
  personRow,
  propertyInput,
  ScheduleAnswer,
  MyRouteAnswer,
  SearchAnswer,
  sessionRow,
  Settings,
  SettingsPatch,
  signInAnswer,
  TeamRole,
  VisitDetail,
  VisitFixBody,
  VisitFutureBody,
} from "@steward/shared";
import type { z } from "zod";
import { api, ApiError, pictureUrl } from "./client";

export type Me = z.infer<typeof meAnswer>;
export type Company = z.infer<typeof companyAnswer>;
export type Landing = z.infer<typeof landingAnswer>;
export type SignInLook = z.infer<typeof signInLookAnswer>;
export type Person = z.infer<typeof personRow>;
export type KeyRow = z.infer<typeof keyRow>;
export type SessionRow = z.infer<typeof sessionRow>;
export type AuditItem = z.infer<typeof auditItem>;
export type OutboxItem = z.infer<typeof outboxItem>;
export type MyVisit = z.infer<typeof myVisit>;
export type CompanySummary = z.infer<typeof companySummaryRow>;
export type SignInAnswer = z.infer<typeof signInAnswer>;
export type Invite = z.infer<typeof inviteAnswer>;
export type InviteInput = z.input<typeof inviteBody>;
export type InvitePeek = z.infer<typeof invitePeek>;
export type ClientInput = z.input<typeof clientInput>;
export type ClientPatch = z.input<typeof clientPatch>;
export type JobInput = z.input<typeof jobInput>;
export type JobPatch = z.input<typeof jobPatch>;
export type LineInput = z.input<typeof lineInput>;
export type PropertyInput = z.input<typeof propertyInput>;
export interface BulkInput {
  ids: string[];
  action: "deactivate" | "reactivate" | "tag" | "untag";
  reason?: z.input<typeof deactivateBody>["reason"];
  tag?: string;
  confirm?: string;
}
/** Where notes and files hang: a client, a job or a visit (by its ref). */
export type NoteOn = "clients" | "jobs" | "visits" | "requests";
export interface Page<T> {
  items: T[];
  next: string | null;
}

/** "a=1&b=2" from the set values only. */
export function qs(o: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const useMe = () =>
  useQuery({ queryKey: ["me"], queryFn: () => api<Me>("GET", "auth/me"), retry: false, staleTime: 60_000 });
const here = () => encodeURIComponent(window.location.origin);
/** The brand at this web address (name, color, logo) for the sign-in pages; null brand is the plain Steward look. */
export const signInLookQuery = {
  queryKey: ["sign-in-look"],
  queryFn: () => api<SignInLook>("GET", `public/sign-in-look?origin=${here()}`),
  staleTime: Infinity,
  retry: false,
};
export const useSignInLook = () => useQuery(signInLookQuery);
/** Which company to start in at this web address, or that this login has none here. */
export const useLanding = () =>
  useQuery({ queryKey: ["landing"], queryFn: () => api<Landing>("GET", `landing?origin=${here()}`), retry: false });
export const useCompanies = () => useQuery({ queryKey: ["companies"], queryFn: () => api<Company[]>("GET", "companies") });
export const useCompany = (id: string | null) =>
  useQuery({ queryKey: ["company", id], queryFn: () => api<Company>("GET", "company"), enabled: Boolean(id) });
export const usePeople = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["people", companyId], queryFn: () => api<Person[]>("GET", "people"), enabled });
export const useSettings = (companyId: string) =>
  useQuery({
    queryKey: ["settings", companyId],
    queryFn: () => api<{ settings: Settings; own: Record<string, unknown> }>("GET", "settings"),
  });
/** True only when the company is known to be in test mode (sending off). People who cannot read settings see no test words. */
export const useTestMode = (companyId: string) => {
  const s = useSettings(companyId);
  return s.data ? !s.data.settings.messaging.live : false;
};
export const useKeys = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["keys", companyId], queryFn: () => api<KeyRow[]>("GET", "keys"), enabled });
/** HQ's Google Maps browser key: one for every company, so it is asked for once. */
/** crew: their own route to the same key (they cannot read the schedule). */
export const useMapsKey = (crew = false) =>
  useQuery({
    queryKey: ["maps-key"],
    queryFn: () => api<{ key: string | null }>("GET", crew ? "my-day/maps-key" : "maps-key"),
    staleTime: Infinity,
    retry: false,
  });
export const useSessions = () => useQuery({ queryKey: ["sessions"], queryFn: () => api<SessionRow[]>("GET", "auth/sessions") });
export const useAudit = (companyId: string) =>
  useQuery({ queryKey: ["audit", companyId], queryFn: () => api<Page<AuditItem>>("GET", "audit?limit=100") });
export const useOutbox = (companyId: string) =>
  useQuery({ queryKey: ["outbox", companyId], queryFn: () => api<Page<OutboxItem>>("GET", "outbox?limit=100") });
export const useMyDay = (companyId: string, date?: string) =>
  useQuery({
    queryKey: ["myday", companyId, date ?? "today"],
    queryFn: () => api<MyVisit[]>("GET", `my-day${qs({ date })}`),
    placeholderData: keepPreviousData,
  });
export const useHome = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["home", companyId], queryFn: () => api<HomeAnswer>("GET", "home"), enabled });
export const useReport = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["report", companyId], queryFn: () => api<CompanySummary[]>("GET", "reports/companies"), enabled });

// Clients, jobs and visits ------------------------------------------------------------------------------------------

export interface ClientsQuery {
  q?: string | undefined;
  status?: "active" | "inactive" | "all";
  tag?: string | undefined;
  sort?: "name" | "newest" | "next_visit" | "last_activity" | "lifetime";
  dir?: "asc" | "desc" | undefined;
  show?: "plan" | "nothing_booked" | "at_risk" | undefined;
}
export const useClients = (companyId: string, q: ClientsQuery, enabled = true) =>
  useQuery({
    queryKey: ["clients", companyId, q],
    queryFn: () => api<Page<ClientRow>>("GET", `clients${qs({ ...q, limit: 200 })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
/** The client list, 100 at a time with Show more. */
export const useClientPages = (companyId: string, q: ClientsQuery) =>
  useInfiniteQuery({
    queryKey: ["clients", companyId, "pages", q],
    queryFn: ({ pageParam }) => api<Page<ClientRow>>("GET", `clients${qs({ ...q, limit: 100, cursor: pageParam })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
  });
export const useClientSummary = (companyId: string, enabled = true) =>
  useQuery({
    queryKey: ["clients", companyId, "summary"],
    queryFn: () => api<ClientSummary>("GET", "clients/summary"),
    enabled,
  });
export const useClient = (id: string, enabled = true) =>
  useQuery({
    queryKey: ["client", id],
    queryFn: () => api<ClientDetail>("GET", `clients/${id}`),
    enabled: enabled && Boolean(id),
  });
export const useClientPricing = (id: string, enabled = true) =>
  useQuery({
    queryKey: ["client", id, "pricing"],
    queryFn: () => api<PricingRow[]>("GET", `clients/${id}/pricing`),
    enabled,
  });
export const useClientReminders = (id: string, enabled = true) =>
  useQuery({
    queryKey: ["client", id, "reminders"],
    queryFn: () => api<ReminderRow[]>("GET", `clients/${id}/reminders`),
    enabled,
  });
export const useAddReminder = () =>
  useChange(
    (v: { clientId: string; reminder: ReminderInput }) =>
      api<{ id: string }>("POST", `clients/${v.clientId}/reminders`, v.reminder),
    ["client", "home"],
  );
export const useSetReminder = () =>
  useChange(
    (v: { clientId: string; id: string; done: boolean }) =>
      api<{ ok: boolean }>("PATCH", `clients/${v.clientId}/reminders/${v.id}`, { done: v.done }),
    ["client"],
  );
/** The header's Email and Text buttons open the client's conversation, made the first time. */
export const useStartThread = () =>
  useChange(
    (v: { clientId: string; channel: "sms" | "email" }) =>
      api<{ id: string }>("POST", `clients/${v.clientId}/thread`, { channel: v.channel }),
    ["inbox"],
  );

export interface JobsQuery {
  q?: string | undefined;
  clientId?: string;
  status?: "active" | "ended" | "all";
  kind?: "repeating" | "one-time" | "all";
}
export const useJobs = (companyId: string, q: JobsQuery, enabled = true) =>
  useQuery({
    queryKey: ["jobs", companyId, q],
    queryFn: () => api<Page<JobRow>>("GET", `jobs${qs({ ...q, limit: 200 })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
/** The Jobs list, 25 at a time: the next page loads as the list scrolls to its end, like Jobber (owner, 2026-10-29). */
export const JOBS_PAGE = 25;
export const useJobPages = (companyId: string, q: JobsQuery) =>
  useInfiniteQuery({
    queryKey: ["jobs", companyId, "pages", q],
    queryFn: ({ pageParam }) =>
      api<Page<JobRow>>("GET", `jobs${qs({ ...q, limit: JOBS_PAGE, ...(pageParam ? { cursor: pageParam } : {}) })}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.next ?? undefined,
    placeholderData: keepPreviousData,
  });
export const useJob = (id: string, enabled = true) =>
  useQuery({ queryKey: ["job", id], queryFn: () => api<JobDetail>("GET", `jobs/${id}`), enabled: enabled && Boolean(id) });
/** The job's expenses, labor and profit (labor and profit null for people who do not see payroll). */
export const useJobCosts = (id: string, enabled = true) =>
  useQuery({
    queryKey: ["job", id, "costs"],
    queryFn: () => api<JobCosts>("GET", `jobs/${id}/costs`),
    enabled: enabled && Boolean(id),
  });

/** The plan offer for a one-time home client: prices for their home and the text (owner, 2026-11-02). */
export const usePlanOffer = (clientId: string, enabled = true) =>
  useQuery({
    queryKey: ["client", clientId, "plan-offer"],
    queryFn: () => api<PlanOfferPreview>("GET", `clients/${clientId}/plan-offer`),
    enabled: enabled && Boolean(clientId),
    retry: false,
  });

export interface VisitsQuery {
  from: string;
  to: string;
  personId?: string;
  unassigned?: boolean;
  clientId?: string;
  jobId?: string;
}
export const visitsKey = (companyId: string, q: VisitsQuery) => ["visits", companyId, q] as const;
export const useVisits = (companyId: string, q: VisitsQuery, enabled = true) =>
  useQuery({
    queryKey: visitsKey(companyId, q),
    queryFn: () => api<ScheduleAnswer>("GET", `visits${qs({ ...q })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
/** A crew member's own visits shaped like the schedule board (api work.controller my-schedule). */
export const useMySchedule = (companyId: string, q: VisitsQuery, enabled = true) =>
  useQuery({
    queryKey: ["myday", companyId, "week", q.from, q.to],
    queryFn: () => api<ScheduleAnswer>("GET", `my-schedule${qs({ ...q })}`),
    placeholderData: keepPreviousData,
    enabled,
  });
/** My stops for a day in driving order with the map line (crew); on says routes are on for the company. */
export const useMyRoute = (companyId: string, date: string) =>
  useQuery({
    queryKey: ["myday", companyId, "route", date],
    queryFn: () => api<MyRouteAnswer>("GET", `my-route?date=${date}`),
    placeholderData: keepPreviousData,
  });
export const useVisit = (ref: string) =>
  useQuery({ queryKey: ["visit", ref], queryFn: () => api<VisitDetail>("GET", `visits/${ref}`) });
export const useChecklists = (companyId: string, enabled = true) =>
  useQuery({ queryKey: ["checklists", companyId], queryFn: () => api<ChecklistRow[]>("GET", "checklists"), enabled });
export const useSearch = (companyId: string, q: string) =>
  useQuery({
    queryKey: ["search", companyId, q],
    queryFn: () => api<SearchAnswer>("GET", `search${qs({ q })}`),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

/** A change that refreshes the lists it touches once it is saved. */
function useChange<I, O>(fn: (input: I) => Promise<O>, refresh: string[], key?: string) {
  const qc = useQueryClient();
  return useMutation({
    ...(key ? { mutationKey: [key] } : {}),
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(refresh.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    },
  });
}

/** What a change to clients, jobs or visits can show up on. */
const WORK = ["clients", "client", "jobs", "job", "visits", "visit", "myday", "home", "search", "report"];

export const useUpdatePerson = () =>
  useChange(
    (v: { id: string; patch: z.infer<typeof personPatch> }) => api<Person>("PATCH", `people/${v.id}`, v.patch),
    ["people", "mypay", "audit"],
  );
export const useSetPersonRole = () =>
  useChange(
    (v: { id: string; role: TeamRole }) => api<Person>("PUT", `people/${v.id}/role`, { role: v.role }),
    ["people", "audit"],
  );
export const useInvite = () => useChange((v: InviteInput) => api<Invite>("POST", "invites", v), ["people", "outbox", "audit"]);
/** Me on this company's team (my circle's color and picture); null when I am not on its team. */
export const useMyPerson = (companyId: string) =>
  useQuery({
    queryKey: ["me-person", companyId],
    queryFn: () =>
      api<MyPerson>("GET", "me/person").catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
  });
/** What shows a person's circle: the team, the schedule and my own. */
const CIRCLES = ["me-person", "people", "visits", "visit", "jobs", "job", "myday", "home"];
export const useSetMyColor = () => useChange((color: string | null) => api<MyPerson>("PATCH", "me/person", { color }), CIRCLES);
export const useSetMyPhoto = () =>
  useChange(
    (v: { name: string; mime: string; dataBase64: string } | null) =>
      v ? api<MyPerson>("POST", "me/person/photo", v) : api<MyPerson>("DELETE", "me/person/photo"),
    CIRCLES,
  );
/** A profile picture as a link an <img> can show; kept for the session, since a new picture gets a new file id. */
export const usePicture = (personId: string | undefined, photo: string | null | undefined) =>
  useQuery({
    queryKey: ["picture", personId, photo],
    queryFn: () => pictureUrl(`people/${personId ?? ""}/photo/${photo ?? ""}`),
    enabled: Boolean(personId && photo),
    staleTime: Infinity,
    gcTime: Infinity,
  });
export const useUpdateCompany = () =>
  useChange(
    (v: { id: string; patch: z.input<typeof companyUpdateBody> }) => api<Company>("PATCH", `companies/${v.id}`, v.patch),
    ["company", "companies", "audit"],
  );
export const useUploadLogo = () =>
  useChange(
    (v: { name: string; mime: string; dataBase64: string; variant: "color" | "white" }) =>
      api<Company>("POST", "company/logo", v),
    ["company", "companies", "audit"],
  );
export const useUpdateSettings = () =>
  useChange((v: SettingsPatch) => api<unknown>("PATCH", "settings", v), ["settings", "payroll", "mypay", "audit"]);
export const useAutomations = () =>
  useQuery({ queryKey: ["automations"], queryFn: () => api<{ automations: AutomationRow[] }>("GET", "automations") });
export const useAutomation = (kind: AutomationKind) =>
  useQuery({ queryKey: ["automations", kind], queryFn: () => api<AutomationDetail>("GET", `automations/${kind}`) });
export const useSaveAutomation = (kind: AutomationKind) =>
  useChange((v: AutomationPatch) => api<AutomationDetail>("PATCH", `automations/${kind}`, v), ["automations", "audit"]);
export const useTerritories = () =>
  useQuery({ queryKey: ["territories"], queryFn: () => api<{ zips: string[] }>("GET", "territories") });
export const useSaveTerritories = () =>
  useChange((zips: string[]) => api<{ zips: string[] }>("PUT", "territories", { zips }), ["territories", "audit"]);
export const useSignups = (enabled = true) =>
  useQuery({ queryKey: ["signups"], queryFn: () => api<{ signups: SignupRow[] }>("GET", "signups"), enabled });
export const useApplicants = () =>
  useQuery({ queryKey: ["applicants"], queryFn: () => api<{ applicants: ApplicantRow[] }>("GET", "applicants") });
/** Move an applicant, say why they are not a fit, score the phone screen (Hiring v2). */
export const useMoveApplicant = () =>
  useChange(
    ({ id, ...body }: { id: string } & ApplicantPatch) => api<ApplicantRow>("PATCH", `applicants/${id}`, body),
    ["applicants"],
  );
/** Text the link to pick a phone screen time. */
export const useTextApplicantLink = () =>
  useChange((id: string) => api<ApplicantRow>("POST", `applicants/${id}/text-link`), ["applicants"]);
/** Text the Not now pool that we are hiring again. */
export const usePoolText = () => useChange(() => api<{ sent: number }>("POST", "applicants/pool-text"), ["applicants"]);
/** Book the paid trial day (Hiring v2 phase 2). */
export const useBookTrial = () =>
  useChange(
    ({ id, ...body }: { id: string; at: string; leadId: string; address?: string; rateCents?: number }) =>
      api<ApplicantRow>("POST", `applicants/${id}/trial`, body),
    ["applicants"],
  );
interface OfferBody {
  startOn: string;
  payType: "percent" | "hourly";
  percent: number | null;
  hourlyCents: number | null;
}
/** The offer letter to start from. */
export const useOfferDraft = () =>
  useChange(
    ({ id, ...body }: { id: string } & OfferBody) => api<{ letter: string }>("POST", `applicants/${id}/offer-draft`, body),
    [],
  );
/** Send the offer to sign. */
/** Hiring phase 3: order the background check from Checkr. */
export const useOrderBackground = () =>
  useChange(
    ({ id, ...body }: { id: string; email?: string }) => api<ApplicantRow>("POST", `applicants/${id}/background`, body),
    ["applicants"],
  );
/** OK to hire or not, after a check that needs a look or one run another way. */
export const useDecideBackground = () =>
  useChange(
    ({ id, ...body }: { id: string; status: "approved" | "declined"; note?: string }) =>
      api<ApplicantRow>("POST", `applicants/${id}/background/decide`, body),
    ["applicants"],
  );
/** The hiring report for a date range. */
export const useHiringReport = (companyId: string, from: string, to: string) =>
  useQuery({
    queryKey: ["hiring-report", companyId, from, to],
    queryFn: () => api<HiringReport>("GET", `hiring/report?from=${from}&to=${to}`),
  });
export const useSendOffer = () =>
  useChange(
    ({ id, ...body }: { id: string; letter: string } & OfferBody) => api<ApplicantRow>("POST", `applicants/${id}/offer`, body),
    ["applicants"],
  );
/** Show the full tax number from their W-9 (written in the audit log). */
export const useApplicantTin = () => useChange((id: string) => api<{ tin: string }>("GET", `applicants/${id}/tin`), []);
/** My refer-a-friend link to the careers page. */
export const useReferLink = (companyId: string) =>
  useQuery({
    queryKey: ["refer-link", companyId],
    queryFn: () => api<{ link: string | null; bonusCents: number; afterDays: number }>("GET", "my/refer-link"),
  });
/** Type in an applicant. */
export const useAddApplicant = () => useChange((v: ApplicantInput) => api<ApplicantRow>("POST", "applicants", v), ["applicants"]);
export const useSmartLists = () =>
  useQuery({ queryKey: ["smart-lists"], queryFn: () => api<{ lists: SmartListRow[] }>("GET", "smart-lists") });
export const useSmartListClients = (id: string) =>
  useQuery({
    queryKey: ["smart-lists", id],
    queryFn: () =>
      api<{ clients: { id: string; name: string; phone: string | null; lastVisit: string | null }[] }>(
        "GET",
        `smart-lists/${id}/clients`,
      ),
  });
export const useAddSmartList = () =>
  useChange((v: SmartListInput) => api<SmartListRow>("POST", "smart-lists", v), ["smart-lists"]);
export const useComplaints = () =>
  useQuery({ queryKey: ["complaints"], queryFn: () => api<{ complaints: ComplaintRow[] }>("GET", "complaints") });
export const useAddComplaint = () => useChange((v: ComplaintInput) => api<ComplaintRow>("POST", "complaints", v), ["complaints"]);
export const useChangeComplaint = () =>
  useChange(
    (v: { id: string; patch: ComplaintPatch }) => api<ComplaintRow>("PATCH", `complaints/${v.id}`, v.patch),
    ["complaints"],
  );
export const useLooseEnds = (enabled = true) =>
  useQuery({ queryKey: ["loose-ends"], queryFn: () => api<{ looseEnds: LooseEnd[] }>("GET", "loose-ends"), enabled });
export const useSnooze = () =>
  useChange((v: { key: string; until: string }) => api<unknown>("POST", "loose-ends/snooze", v), ["loose-ends"]);
export const useHire = () =>
  useChange(
    (v: { id: string; invite: boolean }) => api<ApplicantRow>("POST", `applicants/${v.id}/hire`, { invite: v.invite }),
    ["applicants", "people", "loose-ends"],
  );
export const useRoutes = () => useQuery({ queryKey: ["routes"], queryFn: () => api<{ routes: RouteRow[] }>("GET", "routes") });
export const useAddRoute = () => useChange((v: RouteInput) => api<RouteRow>("POST", "routes", v), ["routes"]);
export const useChangeRoute = () =>
  useChange(
    (v: { id: string; patch: Partial<RouteInput> }) => api<RouteRow>("PATCH", `routes/${v.id}`, v.patch),
    ["routes", "visits"],
  );
export const useRemoveRoute = () =>
  useChange((id: string) => api<{ ok: true; jobs: number }>("DELETE", `routes/${id}`), ["routes", "visits", "day-order"]);
export const useOrderRoute = () =>
  useChange(
    (v: { id: string; jobIds: string[]; date?: string }) =>
      api<RouteRow>("PUT", `routes/${v.id}/order`, { jobIds: v.jobIds, ...(v.date ? { date: v.date } : {}) }),
    ["routes", "visits", "day-order"],
  );
export const useMoveRoute = () =>
  useChange(
    (v: { id: string; from: string; to: string; message: string | null }) =>
      api<{ moved: number; texted: number }>("POST", `routes/${v.id}/move`, { from: v.from, to: v.to, message: v.message }),
    ["routes", "visits", "schedule"],
  );
export const useCapacity = (from: string, to: string) =>
  useQuery({
    queryKey: ["capacity", from, to],
    queryFn: () => api<{ on: boolean; days: CapacityDay[] }>("GET", `capacity?from=${from}&to=${to}`),
  });
export const useTimeOff = (from: string, to: string) =>
  useQuery({
    queryKey: ["time-off", from, to],
    queryFn: () => api<{ timeOff: TimeOffRow[] }>("GET", `time-off?from=${from}&to=${to}`),
  });
export const useAddTimeOff = () =>
  useChange((v: TimeOffInput) => api<TimeOffRow>("POST", "time-off", v), ["time-off", "capacity"]);
export const useClearTimeOff = () =>
  useChange((id: string) => api<unknown>("DELETE", `time-off/${id}`), ["time-off", "capacity"]);
/** A crew member's own time off, today on. */
export const useMyTimeOff = () =>
  useQuery({ queryKey: ["time-off", "mine"], queryFn: () => api<{ timeOff: TimeOffRow[] }>("GET", "my-time-off") });
export const useAddMyTimeOff = () =>
  useChange((v: Omit<TimeOffInput, "personId">) => api<TimeOffRow>("POST", "my-time-off", v), ["time-off", "capacity"]);
export const useClearMyTimeOff = () =>
  useChange((id: string) => api<unknown>("DELETE", `my-time-off/${id}`), ["time-off", "capacity"]);
export const useBestOrder = () =>
  useMutation({ mutationFn: (v: { date: string; refs: string[] }) => api<BestOrderAnswer>("POST", "best-order", v) });
/** The road line through each list of stops, in its order. Asked again only when the stops, their order or spots change. */
export const useRoutePaths = (date: string, lists: { ref: string; lat: number | null; lng: number | null }[][]) =>
  useQueries({
    queries: lists.map((stops) => {
      const on = stops.filter((s) => s.lat !== null && s.lng !== null);
      return {
        queryKey: ["route-path", date, on.map((s) => `${s.ref}@${String(s.lat)},${String(s.lng)}`).join("|")],
        queryFn: () => api<RoutePathAnswer>("POST", "route-path", { date, refs: on.map((s) => s.ref) }),
        enabled: on.length > 1,
        staleTime: 60 * 60 * 1000,
        // A deploy restarts the server for a few seconds; ask again so the map does not keep straight dashes for an hour.
        retry: 4,
        retryDelay: (n: number) => Math.min(2000 * 2 ** n, 15000),
      };
    }),
  });
export const useSaveDayOrder = () =>
  useChange((v: { date: string; refs: string[] }) => api<unknown>("PUT", "day-order", v), ["day-order", "visits"]);
/** Finds and saves map spots for a day's visits that have none; the visits then come back with them. */
export const useMapSpots = () =>
  useChange((v: { date: string }) => api<MapSpotsAnswer>("POST", "map-spots", v), ["visits", "home", "myday"]);
/** Where each person on the clock is now, for the map's green pins (refreshed every minute). */
export const useTeamMap = (on: boolean) =>
  useQuery({
    queryKey: ["team-map"],
    queryFn: () => api<{ spots: TeamSpot[] }>("GET", "team-map"),
    enabled: on,
    refetchInterval: 60_000,
  });
export const useDayOrder = (date: string) =>
  useQuery({ queryKey: ["day-order", date], queryFn: () => api<{ refs: string[] }>("GET", `day-order?date=${date}`) });
export const useDayText = () => useMutation({ mutationFn: (v: DayTextBody) => api<DayTextAnswer>("POST", "day-text", v) });
export const useInboxUnread = () =>
  useQuery({
    queryKey: ["inbox", "unread"],
    queryFn: () => api<{ count: number }>("GET", "inbox/unread"),
    refetchInterval: 60_000,
  });
/** The bell: what this login should know (new requests, approvals, payments; for crew, their jobs). */
export const useAlerts = () =>
  useQuery({ queryKey: ["alerts"], queryFn: () => api<AlertsAnswer>("GET", "alerts"), refetchInterval: 60_000 });
/** The activity feed behind the bell, a page at a time, only the kinds this login picked. */
export const useFeed = (kinds: string[], on: boolean) =>
  useInfiniteQuery({
    queryKey: ["feed", kinds.join(",")],
    queryFn: ({ pageParam }) =>
      api<FeedAnswer>(
        "GET",
        `activity-feed?limit=30&kinds=${encodeURIComponent(kinds.join(","))}${pageParam ? `&cursor=${pageParam}` : ""}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: on && kinds.length > 0,
    refetchInterval: on ? 60_000 : false,
  });
export const useReadAlerts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => api<AlertsAnswer>("POST", "alerts/read", ids ? { ids } : {}),
    onSuccess: (d) => qc.setQueryData(["alerts"], d),
  });
};
export const useThreads = (status: "open" | "done" | "all") =>
  useQuery({
    queryKey: ["inbox", "threads", status],
    queryFn: () => api<{ threads: ThreadRow[] }>("GET", `inbox/threads?status=${status}`),
    refetchInterval: 30_000,
  });
/** One client's conversations, open and done, for the client page's Communication tab. */
export const useClientThreads = (clientId: string, enabled = true) =>
  useQuery({
    queryKey: ["inbox", "threads", "client", clientId],
    queryFn: () => api<{ threads: ThreadRow[] }>("GET", `inbox/threads?status=all&clientId=${clientId}`),
    enabled,
  });
export const useThread = (id: string | null) =>
  useQuery({
    queryKey: ["inbox", "thread", id],
    queryFn: () => api<ThreadDetail>("GET", `inbox/threads/${id ?? ""}`),
    enabled: Boolean(id),
    refetchInterval: 15_000,
  });
export const useReply = () =>
  useChange(
    (v: { id: string; body: string; note: boolean }) =>
      api<ThreadDetail>("POST", `inbox/threads/${v.id}/${v.note ? "note" : "reply"}`, { body: v.body }),
    ["inbox"],
  );
/** Undo send: stops a reply in its first few seconds and gives the words back. */
export const useUndoReply = () =>
  useChange((messageId: string) => api<UndoneMessage>("POST", `inbox/messages/${messageId}/undo`), ["inbox"]);
export const useThreadChange = () =>
  useChange(
    (v: { id: string; status?: "open" | "done"; assignedTo?: string | null }) =>
      api<ThreadRow>("PATCH", `inbox/threads/${v.id}`, { status: v.status, assignedTo: v.assignedTo }),
    ["inbox"],
  );
export const useEunice = (refetchInterval?: number) =>
  useQuery({
    queryKey: ["eunice"],
    queryFn: () => api<EuniceAnswer>("GET", "eunice"),
    ...(refetchInterval ? { refetchInterval } : {}),
  });
export const useAddLesson = () => useChange((text: string) => api<LessonRow>("POST", "eunice/lessons", { text }), ["eunice"]);
export const useJudgeLesson = () =>
  useChange(
    (v: { id: string; status: "approved" | "rejected" }) =>
      api<LessonRow>("PATCH", `eunice/lessons/${v.id}`, { status: v.status }),
    ["eunice"],
  );
export const useSaveKey = () =>
  useChange(
    (v: { provider: string; secret: string }) => api<KeyRow>("PUT", `keys/${v.provider}`, { secret: v.secret }),
    ["keys", "audit"],
  );
export const useRemoveKey = () => useChange((provider: string) => api<unknown>("DELETE", `keys/${provider}`), ["keys", "audit"]);
export const useEndSession = () => useChange((id: string) => api<unknown>("DELETE", `auth/sessions/${id}`), ["sessions"]);

export const useCreateClient = () => useChange((v: ClientInput) => api<ClientDetail>("POST", "clients", v), WORK);
export const useUpdateClient = () =>
  useChange((v: { id: string; patch: ClientPatch }) => api<ClientDetail>("PATCH", `clients/${v.id}`, v.patch), WORK);
export const useDeactivateClient = () =>
  useChange(
    (v: { id: string; body: z.input<typeof deactivateBody> }) => api<ClientDetail>("POST", `clients/${v.id}/deactivate`, v.body),
    WORK,
  );
export const useReactivateClient = () => useChange((id: string) => api<ClientDetail>("POST", `clients/${id}/reactivate`), WORK);
export const useMergeClient = () =>
  useChange((v: { id: string; into: string }) => api<MergeAnswer>("POST", `clients/${v.id}/merge`, { into: v.into }), WORK);
export const useBulkClients = () => useChange((v: BulkInput) => api<BulkAnswer>("POST", "clients/bulk", v), WORK);
export const useAddContact = () =>
  useChange(
    (v: { clientId: string; contact: z.input<typeof contactInput> }) =>
      api<ClientDetail>("POST", `clients/${v.clientId}/contacts`, v.contact),
    ["client", "clients", "search"],
  );
export const useRemoveContact = () =>
  useChange((v: { clientId: string; id: string }) => api<unknown>("DELETE", `contacts/${v.id}`), ["client", "clients", "search"]);
export const useSendPlanOffer = () =>
  useChange(
    (v: { clientId: string; text?: string; leadId?: string }) =>
      api<PlanOfferAnswer>("POST", `clients/${v.clientId}/plan-offer`, {
        ...(v.text ? { text: v.text } : {}),
        ...(v.leadId ? { leadId: v.leadId } : {}),
      }),
    ["client", "quotes", "home", "todo", "requests", "request"],
  );
export const useSaveProperty = () =>
  useChange(
    (v: { clientId: string; id?: string; property: PropertyInput }) =>
      v.id
        ? api<unknown>("PATCH", `properties/${v.id}`, v.property)
        : api<ClientDetail>("POST", `clients/${v.clientId}/properties`, v.property),
    WORK,
  );

export const useAddNote = () =>
  useChange(
    (v: { on: NoteOn; id: string; note: z.input<typeof noteInput> }) => api<unknown>("POST", `${v.on}/${v.id}/notes`, v.note),
    ["client", "job", "visit", "request"],
  );
export const useUpdateNote = () =>
  useChange(
    (v: { id: string; patch: z.input<typeof notePatch> }) => api<unknown>("PATCH", `notes/${v.id}`, v.patch),
    ["client", "job", "visit", "request"],
  );
export const useRemoveNote = () =>
  useChange((id: string) => api<unknown>("DELETE", `notes/${id}`), ["client", "job", "visit", "request"]);
export const useRestoreNote = () =>
  useChange((id: string) => api<unknown>("POST", `notes/${id}/restore`), ["client", "job", "visit", "request"]);
/** Client files go to clients/:id/files; visit photos to visits/:ref/photos. */
export interface UploadInput {
  on: "clients" | "visits";
  id: string;
  file: z.input<typeof fileUpload>;
}
export const useUpload = () =>
  useChange(
    (v: UploadInput) => api<unknown>("POST", `${v.on}/${v.id}/${v.on === "visits" ? "photos" : "files"}`, v.file),
    ["client", "visit"],
    "upload",
  );
/** Files still on their way up, from any Add photos or Open Camera, so the list can show them while a slow phone sends. */
export const useUploading = (on: UploadInput["on"], id: string) =>
  useMutationState({
    filters: { mutationKey: ["upload"], status: "pending" },
    select: (m) => m.state.variables as UploadInput | undefined,
  }).filter((v): v is UploadInput => v?.on === on && v.id === id);
/** Text the client "on my way" from the business line (the visit's crew, or the office on the phone visit page). */
export const useOnMyWay = () =>
  useChange(
    (ref: string) => api<{ minutes: number; text: string }>("POST", `my-day/${encodeURIComponent(ref)}/on-my-way`, {}),
    ["visit"],
  );
export const useRemoveFile = () => useChange((id: string) => api<unknown>("DELETE", `files/${id}`), ["client", "visit"]);

export const useCreateJob = () => useChange((v: JobInput) => api<JobDetail>("POST", "jobs", v), WORK);
export const useUpdateJob = () =>
  useChange((v: { id: string; patch: JobPatch }) => api<JobDetail>("PATCH", `jobs/${v.id}`, v.patch), WORK);
export const useAddJobExpense = () =>
  useChange(
    (v: { jobId: string; body: JobExpenseInput }) => api<JobExpenseRow>("POST", `jobs/${v.jobId}/expenses`, v.body),
    ["job"],
  );
export const useChangeJobExpense = () =>
  useChange((v: { id: string; patch: JobExpensePatch }) => api<JobExpenseRow>("PATCH", `job-expenses/${v.id}`, v.patch), ["job"]);
export const useRemoveJobExpense = () => useChange((id: string) => api<unknown>("DELETE", `job-expenses/${id}`), ["job"]);
export const useRestoreJobExpense = () =>
  useChange((id: string) => api<JobExpenseRow>("POST", `job-expenses/${id}/restore`), ["job"]);

/** Takes a visit's time out of the job's time log (a timer left on), and Undo for it. */
export const useRemoveVisitTime = () => useChange((id: string) => api<unknown>("DELETE", `visits/${id}/time`), WORK);
export const useRestoreVisitTime = () => useChange((id: string) => api<unknown>("POST", `visits/${id}/time/restore`), WORK);

/** Every visit change answers with the visit, its ref now the row's id. */
const useVisitChange = <I>(fn: (v: I) => Promise<VisitDetail>) => useChange(fn, WORK);
export const useMoveVisit = () =>
  useVisitChange((v: { ref: string; date: string; startTime?: string | null; endTime?: string | null }) =>
    api<VisitDetail>("PATCH", `visits/${v.ref}/schedule`, {
      date: v.date,
      ...(v.startTime !== undefined ? { startTime: v.startTime } : {}),
      ...(v.endTime !== undefined ? { endTime: v.endTime } : {}),
    }),
  );
export const useVisitLines = () =>
  useVisitChange((v: { ref: string; lines: LineInput[]; scope: "this" | "future"; credit?: "credit" | "no_credit" }) =>
    api<VisitDetail>("PUT", `visits/${v.ref}/lines`, {
      lines: v.lines,
      scope: v.scope,
      ...(v.credit ? { credit: v.credit } : {}),
    }),
  );
/** Continue an unfinished job on a later day, or undo that while the extra day has no work. */
export const useVisitContinue = () =>
  useVisitChange((v: { ref: string; to?: string; undo?: boolean }) =>
    api<VisitDetail>("POST", `visits/${v.ref}/continue`, v.undo ? { undo: true } : { to: v.to }),
  );
/** A free visit on a one-time job to fix a complaint about a done visit (no charge, no texts). */
export const useVisitFix = () =>
  useVisitChange((v: { ref: string } & VisitFixBody) => {
    const { ref, ...body } = v;
    return api<VisitDetail>("POST", `visits/${ref}/fix`, body);
  });
export const useVisitCrew = () =>
  useVisitChange((v: { ref: string; crew: string[]; scope: "this" | "future" }) =>
    api<VisitDetail>("PUT", `visits/${v.ref}/crew`, { crew: v.crew, scope: v.scope }),
  );
/** Every visit after this one takes the picked settings from it (Apply visit settings to future visits). */
export const useVisitFuture = () =>
  useVisitChange((v: { ref: string } & VisitFutureBody) => {
    const { ref, ...body } = v;
    return api<VisitDetail>("POST", `visits/${ref}/future`, body);
  });
/** Skip, cancel or put back. Skip and cancel send the reason from the cancel sheet (CancelSheet.tsx); none is "not given". */
export const useVisitStatus = () =>
  useVisitChange((v: { ref: string; status: "skipped" | "cancelled" | "scheduled" } & CancelAnswer) => {
    const { ref, ...body } = v;
    return api<VisitDetail>("POST", `visits/${ref}/status`, body);
  });
/** Change why a cancelled or skipped visit is off. */
export const useVisitCancelReason = () =>
  useVisitChange((v: { ref: string } & CancelAnswer) => {
    const { ref, ...body } = v;
    return api<VisitDetail>("PUT", `visits/${ref}/cancel-reason`, body);
  });
/** What the cancel sheet sends. */
export interface CancelAnswer {
  reason?: CancelReason;
  note?: string;
  by?: CancelBy | null;
  feeCents?: number | null;
}
export const useCompleteVisit = () =>
  useVisitChange((v: { ref: string; crewCollected?: "cash" | "company" | "no" }) =>
    api<VisitDetail>("POST", `visits/${v.ref}/complete`, v.crewCollected ? { crewCollected: v.crewCollected } : {}),
  );
export const useReopenVisit = () => useVisitChange((ref: string) => api<VisitDetail>("POST", `visits/${ref}/reopen`));
export const useVisitTimer = () =>
  useVisitChange((v: { ref: string; action: "start" | "stop" }) =>
    api<VisitDetail>("POST", `visits/${v.ref}/timer`, { action: v.action }),
  );
export const useTickChecklist = () =>
  useVisitChange((v: { ref: string; itemId: string; done: boolean; copy: number }) =>
    api<VisitDetail>("POST", `visits/${v.ref}/checklist`, { itemId: v.itemId, done: v.done, copy: v.copy }),
  );
